import styles from './_storeSong.module.scss'
import { useContext, useEffect, useState } from 'react'
import Link from 'next/link'
import { PlayerContext } from '../../../../utils/song_player'
import { storeSongPath } from '../../../../utils/routeHelpers'
import classNames from 'classnames'
import Image from 'next/legacy/image'
import { joinedArtistsStr } from '../../../../utils/songs/artists'
import { playButton } from '../../../../components/SongComponent'
import { t } from '../../../../utils'
import { convertTime } from '../../../../utils/playerHelpers'
import { Song } from '../../../../__generated__/graphql'
import { useRouter } from 'next/router'
import { useAuth } from '../../../../utils/auth'

interface StoreSongProps {
  song: Song
  priority?: boolean
}
const StoreSongs: React.FC<StoreSongProps> = ({ song, priority = false }) => {
  const {
    playerState: { player, isLoading },
    playOrPause,
    stop
  } = useContext(PlayerContext)

  const playerSong = { id: song.id, url: song.sampleUrl }
  const isActive = player.isActiveSong(playerSong)
  const isPaused = player.isPaused(playerSong)
  const isSongLoading = player.isActiveSong(playerSong) && isLoading
  const isSongPlaying = isActive && !isPaused && !isSongLoading
  const hasPlayButton = isPaused || !isActive
  const albumCover = song.album.cover.medium
  const [currentTime, setCurrentTime] = useState<number>(0)

  const isHidden = !song.visibleInStore
  const audio = player.getAudio()
  const router = useRouter()
  const { isAdmin } = useAuth()

  useEffect(() => {
    router.events.on('routeChangeStart', stop)
    return () => router.events.off('routeChangeStart', stop)
  }, [])

  useEffect(() => {
    if (isSongPlaying) {
      const progress = setInterval(() => {
        setCurrentTime(audio?.currentTime)
      }, 1000)

      return () => {
        clearInterval(progress)
      }
    }
  }, [isSongPlaying])

  const getTime = (time) =>
    isActive && time ? convertTime(+time.toFixed(0)) : ''

  return (
    <article
      className={classNames(styles.storeSong, {
        [styles.isStoreSongActive]: isActive
      })}
    >
      <div className={classNames(styles.storeSongImgContainer, 'is-square')}>
        <Link
          href={storeSongPath(song)}
          prefetch={false}
          className={classNames({
            [styles.isDisabled]: isSongPlaying
          })}
        >
          <Image
            src={albumCover}
            alt={`${song.title} | ${joinedArtistsStr(song.artists)}`}
            priority={priority}
            quality={50}
            width={205}
            height={205}
            layout="fill"
            sizes="(max-width: 256px) 66px,
                      (max-width: 384px) 85px,
                      (max-width: 769px) 210px,
                      (max-width: 1024px) 256px,
                      (max-width: 1216px) 240px,
                      (max-width: 1408px) 153px,
                      205px"
          />
        </Link>
        {isAdmin && (
          <>
            {isHidden && (
              <span className={styles.isHiddenTag}>
                <span>{t('Hidden')}</span>
                <IsHiddenTag />
              </span>
            )}
            <EditButton songId={song.id} />
          </>
        )}
      </div>
      <Link
        href={storeSongPath(song)}
        prefetch={false}
        className={classNames(styles.storeSongTitleLink)}
      >
        <strong className={styles.storeSongTitle}>{song.title}</strong>
        <div className={styles.storeSongArtistName}>
          {joinedArtistsStr(song.artists)}
        </div>
      </Link>
      <div className={styles.storeSongPlayer}>
        <div className={styles.storeSongPlayerTime}>{getTime(currentTime)}</div>
        <button
          type="button"
          className={styles.storeSongPlay}
          onClick={() => {
            playOrPause(playerSong)
          }}
        >
          {playButton(isSongLoading, hasPlayButton)}
        </button>
        <div className={styles.storeSongPlayerTime}>
          {getTime(audio?.duration)}
        </div>
      </div>
    </article>
  )
}

export const EditButton = ({ songId }) => (
  <a
    className={styles.editButton}
    href={`${process.env.NEXT_PUBLIC_OLD_URL}/admin/songs/${songId}/store_items`}
  >
    {t('Edit')}
  </a>
)

export const IsHiddenTag = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="141"
    height="141"
    viewBox="0 0 141 141"
  >
    <defs>
      <mask id="b2nfb" width="2" height="2" x="-1" y="-1">
        <path fill="#fff" d="M3 2h133v133H3z" />
        <path d="M136 134.117H136L3.882 2H126c5.514 0 10 4.486 10 10v122.117-.002.002z" />
      </mask>
      <filter
        id="b2nfa"
        width="165"
        height="168"
        x="-13"
        y="-14"
        filterUnits="userSpaceOnUse"
      >
        <feOffset dy="3" in="SourceGraphic" result="FeOffset1023Out" />
        <feGaussianBlur
          in="FeOffset1023Out"
          result="FeGaussianBlur1024Out"
          stdDeviation="2.4 2.4"
        />
      </filter>
      <linearGradient
        id="b2nfc"
        x1="69.94"
        x2="69.94"
        y1="2"
        y2="200.9"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" stopColor="#b700ba" />
        <stop offset="1" stopColor="#49c7f2" />
      </linearGradient>
    </defs>
    <g>
      <g>
        <g filter="url(#b2nfa)">
          <path
            fill="none"
            d="M136 134.117H136L3.882 2H126c5.514 0 10 4.486 10 10v122.117-.002.002z"
            mask='url("#b2nfb")'
          />
          <path
            fillOpacity=".2"
            d="M136 134.117H136L3.882 2H126c5.514 0 10 4.486 10 10v122.117-.002.002z"
          />
        </g>
        <path
          fill="url(#b2nfc)"
          d="M136 134.117H136L3.882 2H126c5.514 0 10 4.486 10 10v122.117-.002.002z"
        />
      </g>
    </g>
  </svg>
)

export default StoreSongs
